import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Loader,
  Paper,
  Stack,
  Table,
  Text,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconEdit,
  IconLayoutList,
  IconPlus,
  IconToolsKitchen2,
  IconTrash,
} from '@tabler/icons-react';
import { PageHeader } from '../../components/PageHeader';
import { EmptyState } from '../../components/EmptyState';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import {
  useDeleteModifierGroup,
  useModifierGroups,
} from './modifierGroupsApi';
import { ModifierGroupFormDrawer } from './ModifierGroupFormDrawer';
import { ManageModifiersDrawer } from './ManageModifiersDrawer';
import type { ModifierGroupDto } from './types';

export function ModifierGroupsPage() {
  const { t } = useTranslation();
  const { data: groups, isLoading } = useModifierGroups();
  const deleteMutation = useDeleteModifierGroup();

  const [groupDrawerOpen, setGroupDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<ModifierGroupDto | null>(null);
  const [deleting, setDeleting] = useState<ModifierGroupDto | null>(null);

  const [manageDrawerOpen, setManageDrawerOpen] = useState(false);
  // We hold the *id* and resolve against fresh data so the manage drawer
  // shows updated modifiers right after add/edit/delete.
  const [managingId, setManagingId] = useState<string | null>(null);
  const managing = useMemo(
    () => groups?.find((g) => g.modifierGroupId === managingId) ?? null,
    [groups, managingId],
  );

  const sortedGroups = useMemo(
    () =>
      [...(groups ?? [])].sort((a, b) => {
        if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
        return a.nameAr.localeCompare(b.nameAr);
      }),
    [groups],
  );

  const openCreate = () => {
    setEditing(null);
    setGroupDrawerOpen(true);
  };
  const openEdit = (group: ModifierGroupDto) => {
    setEditing(group);
    setGroupDrawerOpen(true);
  };
  const openManage = (group: ModifierGroupDto) => {
    setManagingId(group.modifierGroupId);
    setManageDrawerOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await deleteMutation.mutateAsync(deleting.modifierGroupId);
      notifications.show({
        color: 'green',
        title: t('common.toasts.deleted'),
        message: deleting.nameAr,
      });
      setDeleting(null);
    } catch (err) {
      notifications.show({
        color: 'red',
        title: t('common.toasts.errorTitle'),
        message: apiMessage(err) ?? t('common.toasts.errorBody'),
      });
    }
  };

  const hasGroups = sortedGroups.length > 0;

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('nav.sections.catalog')}
        title={t('modifierGroups.page.title')}
        subtitle={t('modifierGroups.page.subtitle')}
        actions={
          <Button leftSection={<IconPlus size={16} stroke={1.8} />} onClick={openCreate}>
            {t('modifierGroups.page.create')}
          </Button>
        }
      />

      <Paper p={hasGroups ? 0 : 'lg'}>
        {isLoading ? (
          <Stack align="center" py={48}>
            <Loader size="sm" />
          </Stack>
        ) : !hasGroups ? (
          <EmptyState
            icon={<IconToolsKitchen2 size={22} stroke={1.6} />}
            title={t('modifierGroups.empty.title')}
            description={t('modifierGroups.empty.body')}
            actionLabel={t('modifierGroups.page.create')}
            onAction={openCreate}
          />
        ) : (
          <Table verticalSpacing="md" highlightOnHover striped="even">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('modifierGroups.columns.name')}</Table.Th>
                <Table.Th>{t('modifierGroups.columns.rules')}</Table.Th>
                <Table.Th>{t('modifierGroups.columns.modifiers')}</Table.Th>
                <Table.Th>{t('modifierGroups.columns.sort')}</Table.Th>
                <Table.Th style={{ width: 220, textAlign: 'end' }}>
                  {t('common.actions')}
                </Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {sortedGroups.map((group) => (
                <Table.Tr key={group.modifierGroupId}>
                  <Table.Td>
                    <Text fw={500} c="var(--wheels-ink-strong)">
                      {group.nameAr}
                    </Text>
                    {group.nameEn && (
                      <Text fz="xs" c="var(--wheels-ink-muted)">
                        {group.nameEn}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4}>
                      {group.isRequired && (
                        <Badge color="wheels" variant="light" radius="sm" size="sm">
                          {t('modifierGroups.required')}
                        </Badge>
                      )}
                      <Badge color="gray" variant="light" radius="sm" size="sm" className="font-numeric">
                        {formatSelections(group.minSelections, group.maxSelections, t)}
                      </Badge>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Badge variant="light" color="gray" radius="sm" className="font-numeric">
                      {t('modifierGroups.modifiersCount', { count: group.modifiers.length })}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text fz="sm" className="font-numeric">
                      {group.sortOrder}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs" justify="flex-end" wrap="nowrap">
                      <Button
                        size="xs"
                        variant="light"
                        color="gray"
                        leftSection={<IconLayoutList size={14} stroke={1.6} />}
                        onClick={() => openManage(group)}
                      >
                        {t('modifierGroups.manage')}
                      </Button>
                      <Tooltip label={t('common.edit')} withinPortal>
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          onClick={() => openEdit(group)}
                          aria-label={t('common.edit')}
                        >
                          <IconEdit size={16} stroke={1.6} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label={t('common.delete')} withinPortal>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          onClick={() => setDeleting(group)}
                          aria-label={t('common.delete')}
                        >
                          <IconTrash size={16} stroke={1.6} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Paper>

      <ModifierGroupFormDrawer
        opened={groupDrawerOpen}
        onClose={() => setGroupDrawerOpen(false)}
        group={editing}
      />

      <ManageModifiersDrawer
        opened={manageDrawerOpen}
        onClose={() => {
          setManageDrawerOpen(false);
          setManagingId(null);
        }}
        group={managing}
      />

      <ConfirmDialog
        opened={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={confirmDelete}
        title={t('modifierGroups.delete.title')}
        message={t('modifierGroups.delete.body', { name: deleting?.nameAr ?? '' })}
        loading={deleteMutation.isPending}
      />
    </Stack>
  );
}

function formatSelections(
  min: number,
  max: number,
  t: (k: string, opts?: Record<string, unknown>) => string,
): string {
  // 0/0 means "no constraint" — let the operator pick freely.
  if (min === 0 && max === 0) return t('modifierGroups.selections.any');
  if (min === max) return t('modifierGroups.selections.exact', { count: min });
  if (max === 0) return t('modifierGroups.selections.atLeast', { count: min });
  return t('modifierGroups.selections.range', { min, max });
}

function apiMessage(err: unknown): string | null {
  if (typeof err === 'object' && err !== null && 'response' in err) {
    const data = (err as { response?: { data?: { detail?: string; title?: string } } })
      .response?.data;
    return data?.detail ?? data?.title ?? null;
  }
  return null;
}
